import {
  ApiRef,
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import {
  PmasApiService,
  Endpoint,
  PossibleIntegrationTypes,
  DefaultResponse,
  IntegrationUpsertPayload,
  WatchdogTypePossibilities,
  RedirectsUsersMeType,
  DetailedRedirect,
  Redirect,
  RedirectPayload,
  ScheduleJob,
  ExportRedirectsParams,
  UploadRedirectsAsyncPayload,
  UploadRedirectsAsyncResponse,
  ImportJobDetails,
  VectorAnalyzeLogsRequest,
  VectorAnalyzeLogsResponse,
  VectorCheckConfigParams,
  VectorCheckConfigResponse,
  VectorDatadogLogsResponse,
  VectorDatadogServicesResponse,
  VectorDeleteConfigPayload,
  VectorDeleteConfigResponse,
  VectorListConfigsResponse,
  VectorPreviewYamlPayload,
  VectorPreviewYamlResponse,
  VectorSubmitPayload,
  VectorSubmitResponse,
  VectorValidateYamlPayload,
  VectorValidateYamlResponse,
  GetRedirectsParams,
  PaginatedRedirectsResponse,
  DnsRecordsResult,
  SslCertificateResult,
  SslBatchExpiryResult,
  UrlStatusResult,
} from '@mercedes-benz/pmas-common';
import { IntegrationsApi } from './integrationsApi';
import { UrlGroupsApi } from './urlGroupsApi';
import { WatchdogApi } from './watchdogApi';
import { AkamaiApi } from './akamaiApi';
import { RaasApi } from './raasApi';
import { CalendarApi } from './calendarApi';
import { VectorApi } from './vectorApi';
import { submitRequest } from './submitRequest';

export const pmasApiRef: ApiRef<PmasApiService> = createApiRef<PmasApiService>({
  id: 'plugin.pmas.api',
});

export class PmasApiClient implements PmasApiService {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly integrationsApi: IntegrationsApi;
  private readonly urlGroupsApi: UrlGroupsApi;
  private readonly watchdogApi: WatchdogApi;
  private readonly akamaiApi: AkamaiApi;
  private readonly raasApi: RaasApi;
  private readonly calendarApi: CalendarApi;
  private readonly vectorApi: VectorApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi?: FetchApi }) {
    const { discoveryApi, fetchApi = { fetch } } = options;
    this.discoveryApi = discoveryApi;
    this.fetchApi = fetchApi;
    this.integrationsApi = new IntegrationsApi(discoveryApi, fetchApi);
    this.urlGroupsApi = new UrlGroupsApi(discoveryApi, fetchApi);
    this.watchdogApi = new WatchdogApi(discoveryApi, fetchApi);
    this.akamaiApi = new AkamaiApi(discoveryApi, fetchApi);
    this.raasApi = new RaasApi(discoveryApi, fetchApi);
    this.calendarApi = new CalendarApi(discoveryApi, fetchApi);
    this.vectorApi = new VectorApi(discoveryApi, fetchApi);
  }

  getTenants() {
    return this.integrationsApi.getTenants();
  }

  getMemberTenants(mbagId: string) {
    return this.integrationsApi.getMemberTenants(mbagId);
  }

  getAllTenantOverview() {
    return this.integrationsApi.getAllTenantOverview();
  }

  getTenantIntegrations(name: string) {
    return this.integrationsApi.getTenantIntegrations(name);
  }

  enableTenantIntegrations(name: string, status: string) {
    return this.integrationsApi.enableTenantIntegrations(name, status);
  }

  getIntegration(integrationId: number, integrationType: Endpoint) {
    return this.integrationsApi.getIntegration(integrationId, integrationType);
  }

  getIntegrations(integrationType: Endpoint) {
    return this.integrationsApi.getIntegrations(integrationType);
  }

  getIntegrationsPerTenant<T = PossibleIntegrationTypes>(
    tenant: string,
    integrationType: Endpoint,
  ): Promise<DefaultResponse<T>> {
    return this.integrationsApi.getIntegrationsPerTenant(
      tenant,
      integrationType,
    );
  }

  getDatasources(integrationType: Endpoint) {
    return this.integrationsApi.getDatasources(integrationType);
  }

  createIntegration(
    integrationType: Endpoint,
    integration: IntegrationUpsertPayload,
  ) {
    return this.integrationsApi.createIntegration(integrationType, integration);
  }

  updateIntegration(
    integrationType: Endpoint,
    integrationId: number,
    integration: IntegrationUpsertPayload,
  ) {
    return this.integrationsApi.updateIntegration(
      integrationType,
      integrationId,
      integration,
    );
  }

  deleteIntegration(integrationId: number, integrationType: Endpoint) {
    return this.integrationsApi.deleteIntegration(
      integrationId,
      integrationType,
    );
  }

  getUrlGroups() {
    return this.urlGroupsApi.getUrlGroups();
  }

  createUrlGroup(urlGroupProps: object) {
    return this.urlGroupsApi.createUrlGroup(urlGroupProps);
  }

  updateUrlGroup(urlGroupId: number, urlGroupProps: object) {
    return this.urlGroupsApi.updateUrlGroup(urlGroupId, urlGroupProps);
  }

  deleteUrlGroup(urlGroupId: number) {
    return this.urlGroupsApi.deleteUrlGroup(urlGroupId);
  }

  addUrlsToUrlGroup(urlGroupId: number, urls: string) {
    return this.urlGroupsApi.addUrlsToUrlGroup(urlGroupId, urls);
  }

  deleteUrlsFromUrlGroup(urlGroupId: number, url: string) {
    return this.urlGroupsApi.deleteUrlsFromUrlGroup(urlGroupId, url);
  }

  getWatchdogStats() {
    return this.watchdogApi.getStats();
  }

  getWatchdogSearch(type: WatchdogTypePossibilities, query: string) {
    return this.watchdogApi.getWatchdogSearch(type, query);
  }

  getAkamaiCpCodes() {
    return this.akamaiApi.getAkamaiCpCodes();
  }

  getAkamaiCpCodesPerTenant(tenant: string) {
    return this.akamaiApi.getAkamaiCpCodesPerTenant(tenant);
  }

  invalidateCpCodes(
    cpCodeIds: number[],
    network: 'staging' | 'production',
  ): Promise<DefaultResponse<any>> {
    return this.akamaiApi.invalidateCpCodes(cpCodeIds, network);
  }

  getMe(): Promise<RedirectsUsersMeType> {
    return this.raasApi.getMe();
  }

  getRedirects(
    params?: GetRedirectsParams,
    options?: { signal?: AbortSignal },
  ): Promise<PaginatedRedirectsResponse> {
    return this.raasApi.getRedirects(params, options);
  }

  getRedirect(redirectId: number): Promise<DetailedRedirect> {
    return this.raasApi.getRedirect(redirectId);
  }

  createRedirect(redirect: RedirectPayload): Promise<Redirect> {
    return this.raasApi.createRedirect(redirect);
  }

  updateRedirect(
    redirectId: number,
    redirect: Partial<RedirectPayload>,
  ): Promise<Redirect> {
    return this.raasApi.updateRedirect(redirectId, redirect);
  }

  deleteRedirect(redirectId: number): Promise<unknown> {
    return this.raasApi.deleteRedirect(redirectId);
  }

  deleteRedirectsBulk(ids: number[]): Promise<unknown> {
    return this.raasApi.deleteRedirectsBulk(ids);
  }

  exportRedirects(params: ExportRedirectsParams): Promise<Response> {
    return this.raasApi.exportRedirects(params);
  }

  exportRedirectsTemplate(): Promise<Response> {
    return this.raasApi.exportRedirectsTemplate();
  }

  uploadRedirectsAsync(
    payload: UploadRedirectsAsyncPayload,
  ): Promise<UploadRedirectsAsyncResponse> {
    return this.raasApi.uploadRedirectsAsync(payload);
  }

  getImportJobDetails(importJobId: string): Promise<ImportJobDetails> {
    return this.raasApi.getImportJobDetails(importJobId);
  }

  getCalendar(): Promise<ScheduleJob[]> {
    return this.calendarApi.getCalendar();
  }

  getVectorCheckConfig(
    params: VectorCheckConfigParams,
  ): Promise<VectorCheckConfigResponse> {
    return this.vectorApi.checkConfig(params);
  }

  submitVectorConfig(
    payload: VectorSubmitPayload,
  ): Promise<VectorSubmitResponse> {
    return this.vectorApi.submitConfig(payload);
  }

  listVectorConfigs(tenants: string[]): Promise<VectorListConfigsResponse> {
    return this.vectorApi.listConfigs(tenants);
  }

  previewVectorYaml(
    payload: VectorPreviewYamlPayload,
    signal?: AbortSignal,
  ): Promise<VectorPreviewYamlResponse> {
    return this.vectorApi.previewYaml(payload, signal);
  }

  validateVectorYaml(
    payload: VectorValidateYamlPayload,
    signal?: AbortSignal,
  ): Promise<VectorValidateYamlResponse> {
    return this.vectorApi.validateYaml(payload, signal);
  }

  deleteVectorConfig(
    payload: VectorDeleteConfigPayload,
  ): Promise<VectorDeleteConfigResponse> {
    return this.vectorApi.deleteConfig(payload);
  }

  getVectorDatadogLogs(params: {
    tenantId: string;
    from?: string;
    to?: string;
    services?: string;
    limit?: number;
  }): Promise<VectorDatadogLogsResponse> {
    return this.vectorApi.getDatadogLogs(params);
  }

  getVectorDatadogServices(params: {
    tenantId: string;
    from?: string;
    to?: string;
  }): Promise<VectorDatadogServicesResponse> {
    return this.vectorApi.getDatadogServices(params);
  }

  analyzeVectorLogs(
    payload: VectorAnalyzeLogsRequest,
  ): Promise<VectorAnalyzeLogsResponse> {
    return this.vectorApi.analyzeLogs(payload);
  }

  getDnsRecords(domain: string): Promise<DnsRecordsResult> {
    return submitRequest({
      discoveryApi: this.discoveryApi,
      fetchApi: this.fetchApi,
      method: 'GET',
      path: `/dns-records?domain=${encodeURIComponent(domain)}`,
    });
  }

  getSslCertificate(domain: string): Promise<SslCertificateResult> {
    return submitRequest({
      discoveryApi: this.discoveryApi,
      fetchApi: this.fetchApi,
      method: 'GET',
      path: `/ssl-certificate?domain=${encodeURIComponent(domain)}`,
    });
  }

  getSslBatchExpiry(domains: string[]): Promise<SslBatchExpiryResult> {
    return submitRequest({
      discoveryApi: this.discoveryApi,
      fetchApi: this.fetchApi,
      method: 'POST',
      path: '/ssl-certificate/batch-expiry',
      body: { domains },
    });
  }

  checkUrlStatus(url: string): Promise<UrlStatusResult> {
    return submitRequest({
      discoveryApi: this.discoveryApi,
      fetchApi: this.fetchApi,
      method: 'POST',
      path: '/performanceUrls/status',
      body: { url },
    });
  }
}
